import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useAccountContext } from "./useAccountContext";
import {
  GET_AVAILABLE_ACCOUNT_CONTEXTS,
  GetAvailableAccountContextsData,
} from "common/graphql/queries/GetAvailableAccountContexts";

/**
 * Null-rendering component that owns the `AvailableAccountContexts` query and
 * stale-ID validation.
 *
 * **Why a separate component?**
 * `AccountContextProvider` is a pure state manager with no Apollo dependency,
 * which allows it to sit above `AuthorizedApolloProvider`. This component bridges
 * the gap: it lives inside `AuthorizedApolloProvider` (so it can use Apollo Client)
 * while still reading and writing `AccountContext` (which flows down from above).
 *
 * **Behaviour:**
 * After the query resolves, if the current `contextId` is not present in the returned
 * list (e.g. the account was deprovisioned or permissions changed), `setContextId(null)`
 * is called to clear the stale value from both React state and localStorage silently.
 *
 * The query result is written to the Apollo cache so {@link AccountContextDropdown}
 * receives it immediately with no additional network request.
 *
 * @example
 * ```tsx
 * // apps/curbsuite/src/index.tsx
 * <AccountContextProvider>
 *   <AuthorizedApolloProvider>
 *     <AccountContextInitializer />  // ← mount once, near the root
 *     <App />
 *   </AuthorizedApolloProvider>
 * </AccountContextProvider>
 * ```
 */
export const AccountContextInitializer: React.FC = () => {
  const { contextId, setContextId } = useAccountContext();
  const { data, loading } = useQuery<GetAvailableAccountContextsData>(GET_AVAILABLE_ACCOUNT_CONTEXTS);

  useEffect(() => {
    if (loading || !data || contextId === null) {
      return;
    }
    const isValid = data.availableAccountContexts.some((ctx) => ctx.id === contextId);
    if (!isValid) {
      setContextId(null);
    }
  }, [data, loading, contextId, setContextId]);

  return null;
};

export default AccountContextInitializer;
